import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  UserGroupIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ChartBarSquareIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { loanOfficerApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';

// HMDA code labels for the LO detail panel — mirror the backend's code tables in
// engine/modules/hmda-lar/edits/utils/reference-data.ts (RACE, LOAN_PURPOSE).
const RACE_LABELS: Record<string, string> = {
  '1': 'American Indian or Alaska Native', '2': 'Asian', '21': 'Asian Indian', '22': 'Chinese',
  '23': 'Filipino', '24': 'Japanese', '25': 'Korean', '26': 'Vietnamese', '27': 'Other Asian',
  '3': 'Black or African American', '4': 'Native Hawaiian or Other Pacific Islander',
  '41': 'Native Hawaiian', '42': 'Guamanian or Chamorro', '43': 'Samoan', '44': 'Other Pacific Islander',
  '5': 'White', '6': 'Information not provided', '7': 'Not applicable',
};
const PURPOSE_LABELS: Record<string, string> = {
  '1': 'Home purchase', '2': 'Home improvement', '31': 'Refinancing',
  '32': 'Cash-out refinancing', '4': 'Other purpose', '5': 'Not applicable',
};
const withLabel = (code: string, labels: Record<string, string>) =>
  labels[code] ? `${labels[code]} (${code})` : code;

export default function LoanOfficerMonitoringPage() {
  const [selectedLO, setSelectedLO] = useState<string | null>(null);
  const [loanSource, setLoanSource] = useState<'hmda' | 'alert'>('hmda');
  // The detail endpoint reads HMDA LAR rows only; there is no per-officer ALERT/ILDR detail
  // endpoint, so ALERT rows used to 404 on click (2026-10-17 module review).
  const detailAvailable = loanSource === 'hmda';

  // Dashboard
  const { data: dashboardData, isLoading: dashLoading } = useQuery({
    queryKey: ['lo-dashboard', loanSource],
    queryFn: () => loanOfficerApi.getDashboard(loanSource),
  });
  const dashboard = dashboardData?.data;
  const officers = dashboard?.loanOfficers || dashboard?.officers || [];

  // Individual LO detail
  const { data: loDetailData, isLoading: loDetailLoading } = useQuery({
    queryKey: ['lo-detail', selectedLO],
    queryFn: () => loanOfficerApi.getLoanOfficer(selectedLO!),
    enabled: !!selectedLO && detailAvailable,
  });
  const loDetail = loDetailData?.data;

  const summaryStats = dashboard?.summary || {};

  return (
    <FeatureGate feature="lo_performance_monitoring" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Loan Officer Monitoring</h1>
            <p className="mt-1 text-sm text-gray-500">
              Monitor loan officer performance, denial rates, and fair lending compliance
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {loanSource === 'hmda'
                ? 'Total Applications includes all HMDA action codes; Originations + Denials may not sum to Applications because withdrawn, incomplete, and approved-not-accepted records are also counted.'
                : 'ALERT / ILDR source: per-officer & per-branch rate-disparity on the non-HMDA book (consumer / SB / SF), using proxied sex/ethnicity and raw interest rate.'}
            </p>
          </div>
          {/* Loan source toggle — HMDA LAR vs the ALERT/ILDR non-HMDA book */}
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {(['hmda', 'alert'] as const).map((src) => (
              <button
                key={src}
                type="button"
                onClick={() => { setLoanSource(src); setSelectedLO(null); }}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  loanSource === src
                    ? 'bg-ocean-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {src === 'hmda' ? 'HMDA LAR' : 'ALERT / ILDR'}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-blue-500" />
              <p className="text-xs font-medium text-gray-500">Total Loan Officers</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {summaryStats.totalOfficers ?? officers.length}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              <p className="text-xs font-medium text-gray-500">Officers with Alerts</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-red-600">
              {summaryStats.officersWithAlerts ?? 0}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-green-500" />
              <p className="text-xs font-medium text-gray-500">Total Applications</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {summaryStats.totalApplications ?? 0}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ChartBarSquareIcon className="h-5 w-5 text-orange-500" />
              <p className="text-xs font-medium text-gray-500">Avg Denial Rate</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {summaryStats.avgDenialRate != null
                ? `${summaryStats.avgDenialRate.toFixed(1)}%`
                : '--'}
            </p>
          </div>
        </div>

        {/* Selected LO Detail */}
        {selectedLO && detailAvailable && (
          <div className="rounded-lg border bg-white">
            <div className="border-b px-4 py-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedLO(null)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Loan Officer Detail: {loDetail?.nmlsrId || selectedLO}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {loDetail?.name || 'Individual performance metrics and demographic analysis'}
                  </p>
                </div>
              </div>
            </div>

            {loDetailLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : loDetail ? (
              <div className="space-y-6 p-4">
                {/* Individual Stats — field names match GET /hmda/loan-officers/:nmlsrId
                    (totalApplications, demographicBreakdown, productMix.purposeCode);
                    the panel previously read names the backend never sent (2026-10-17 module review). */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="rounded-lg border bg-gray-50 p-3">
                    <p className="text-xs font-medium text-gray-500">Applications</p>
                    <p className="mt-0.5 text-xl font-bold text-gray-900">{loDetail.totalApplications ?? 0}</p>
                  </div>
                  <div className="rounded-lg border bg-gray-50 p-3">
                    <p className="text-xs font-medium text-gray-500">Originations</p>
                    <p className="mt-0.5 text-xl font-bold text-green-600">{loDetail.originations ?? 0}</p>
                  </div>
                  <div className="rounded-lg border bg-gray-50 p-3">
                    <p className="text-xs font-medium text-gray-500">Denials</p>
                    <p className="mt-0.5 text-xl font-bold text-red-600">{loDetail.denials ?? 0}</p>
                  </div>
                  <div className="rounded-lg border bg-gray-50 p-3">
                    <p className="text-xs font-medium text-gray-500">Denial Rate</p>
                    <p className="mt-0.5 text-xl font-bold text-gray-900">
                      {loDetail.denialRate != null ? `${loDetail.denialRate.toFixed(1)}%` : '--'}
                    </p>
                    {loDetail.institutionalDenialRate != null && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        Institution: {loDetail.institutionalDenialRate.toFixed(1)}%
                      </p>
                    )}
                  </div>
                </div>

                {/* Demographic Breakdown (applicant race 1) */}
                {loDetail.demographicBreakdown && loDetail.demographicBreakdown.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Demographic Breakdown</h4>
                    <div className="mt-2 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Applicant Race</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Applications</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Denials</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Denial Rate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {loDetail.demographicBreakdown.map((d: any, i: number) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{withLabel(String(d.raceCode), RACE_LABELS)}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{d.applications}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-red-600">{d.denials}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                                {d.denialRate != null ? `${d.denialRate.toFixed(1)}%` : '--'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Denial Reasons */}
                {loDetail.denialReasons && loDetail.denialReasons.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Denial Reasons</h4>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {loDetail.denialReasons.map((dr: any, i: number) => (
                        <div key={i} className="flex items-center justify-between rounded-lg border bg-gray-50 px-3 py-2">
                          <span className="text-sm text-gray-700">{dr.reason}</span>
                          <span className="text-sm font-medium text-gray-900">{dr.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product Mix */}
                {loDetail.productMix && loDetail.productMix.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Product Mix</h4>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {loDetail.productMix.map((pm: any, i: number) => {
                        // Backend returns { purposeCode, count }; share is derived for display.
                        const pct = loDetail.totalApplications > 0 ? (pm.count / loDetail.totalApplications) * 100 : null;
                        return (
                          <div key={i} className="rounded-lg border bg-gray-50 px-3 py-2">
                            <p className="text-xs text-gray-500">{withLabel(String(pm.purposeCode), PURPOSE_LABELS)}</p>
                            <p className="text-sm font-medium text-gray-900">{pm.count} ({pct != null ? `${pct.toFixed(1)}%` : '--'})</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-gray-400">
                No detail data available for this loan officer.
              </div>
            )}
          </div>
        )}

        {/* LO Dashboard Table */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Loan Officer Dashboard</h3>
            {!detailAvailable && (
              <p className="mt-0.5 text-xs text-gray-500">
                Officer detail is available for HMDA LAR data only. ALERT / ILDR results are shown in this table.
              </p>
            )}
          </div>

          {dashLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : officers.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-lg font-medium">No loan officer data</p>
              {loanSource === 'alert' ? (
                <>
                  <p className="mt-1 text-sm">
                    No ALERT / ILDR loans ingested for this team yet — upload a loan trial balance
                    to monitor officers and branches on your non-HMDA book.
                  </p>
                  <Link
                    to="/alert/upload"
                    className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                  >
                    Upload an ALERT file →
                  </Link>
                </>
              ) : (
                <p className="mt-1 text-sm">Upload HMDA data to populate loan officer metrics.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">NMLSR ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Applications</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Originations</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Denials</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Denial Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Rate Spread</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Demo Completeness %</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Alerts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {officers.map((lo: any) => (
                    <tr
                      key={lo.nmlsrId}
                      className={clsx('hover:bg-gray-50', detailAvailable && 'cursor-pointer')}
                      onClick={detailAvailable ? () => setSelectedLO(lo.nmlsrId) : undefined}
                    >
                      <td className={clsx(
                        'whitespace-nowrap px-4 py-3 text-sm font-medium',
                        detailAvailable ? 'text-blue-600 hover:text-blue-800' : 'text-gray-900',
                      )}>
                        {lo.nmlsrId && lo.nmlsrId.toLowerCase() !== 'unknown'
                          ? lo.nmlsrId
                          : <span className="italic text-gray-400">No NMLSR ID</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 tabular-nums">{lo.totalApplications ?? lo.applications ?? 0}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-green-600 tabular-nums">{lo.originations ?? 0}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-red-600 tabular-nums">{lo.denials ?? 0}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                        {lo.denialRate != null ? `${lo.denialRate.toFixed(1)}%` : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {lo.avgRateSpread != null ? `${lo.avgRateSpread.toFixed(2)}%` : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-16 rounded-full bg-gray-200">
                            <div
                              className={clsx(
                                'h-2 rounded-full',
                                (lo.demographicCompleteness || 0) >= 90 ? 'bg-green-500' :
                                (lo.demographicCompleteness || 0) >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                              )}
                              style={{ width: `${Math.min(lo.demographicCompleteness || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">
                            {lo.demographicCompleteness != null ? `${lo.demographicCompleteness.toFixed(0)}%` : '--'}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {(lo.alertCount || 0) > 0 ? (
                          <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                            {lo.alertCount}
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            0
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}
